import React from 'react';
import { Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../../i18n/config';
import { Card } from '../../ui/card';
import { useAppSelector } from '../../../redux/store';

/**
 * Welcome message for the chat
 * Shows organization info and chat welcome
 */
const WelcomeMessage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';
  const organization = useAppSelector(state => state.organization.currentOrganization);

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 gap-6">
      {/* Organization Welcome Card */}
      {organization && (
        <>
          {/* Organization Logo */}
          {organization.logoUrl && (
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/10 blur-2xl rounded-full" />
                <div className="relative bg-background/80 backdrop-blur-sm border-2 border-gold/20 rounded-xl p-6 shadow-lg">
                  <img
                    src={organization.logoUrl}
                    alt={organization.name}
                    className="h-24 w-auto max-w-[240px] object-contain"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Organization Name */}
          <h1 className="text-2xl font-bold text-foreground">
            {organization.name}
          </h1>
        </>
      )}

      {/* Chat Welcome Card */}
      <Card
        dir={getLanguageDirection(i18n.language)}
        className="p-6 max-w-lg w-full rounded-2xl bg-gradient-to-br from-gold/8 to-gold/4 border-gold/20 shadow-lg overflow-hidden relative"
      >
        {/* Background decorative element */}
        <div
          className={`absolute ${isRTL ? 'right-[-20px]' : 'left-[-20px]'} top-[-20px] w-[120px] h-[120px] rounded-full`}
          style={{
            background: 'radial-gradient(circle, hsl(var(--gold) / 0.18) 0%, hsl(var(--gold) / 0.05) 70%, transparent 100%)'
          }}
        />

        {/* Content */}
        <div className="relative z-10">
          {/* Icon and title row */}
          <div className="flex items-center mb-3">
            <div className={`flex items-center justify-center bg-gold/12 border border-gold/26 text-gold w-10 h-10 rounded-xl ${isRTL ? 'ml-3' : 'mr-3'}`}>
              <Scale size={22} />
            </div>

            <div>
              <h2 className={`text-lg font-semibold text-foreground tracking-tight ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('components.chats.welcomeMessage.title')}
              </h2>
            </div>
          </div>

          {/* Description */}
          <p className={`${isRTL ? 'mr-[52px] text-right' : 'ml-[52px] text-left'} text-sm text-foreground/75 leading-relaxed`}>
            {t('components.chats.welcomeMessage.description')}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default WelcomeMessage;
